import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

/**
 * ProtectedRoute component that checks if user has required permissions
 * before rendering the child component.
 * 
 * @param {Object} props
 * @param {string|string[]} props.requiredPermissions - Permission(s) required to access this route
 * @param {React.ReactNode} props.children - Component to render if user has permission
 * @param {string} props.redirectTo - Path to redirect to if permission is missing (default: /admin/dashboard)
 */
const ProtectedRoute = ({
    requiredPermissions,
    children,
    redirectTo = '/admin/dashboard'
}) => {
    const navigate = useNavigate();

    useEffect(() => {
        // Get user data from localStorage
        const loginData = JSON.parse(localStorage.getItem('User'));

        if (!loginData || !loginData.permissions) {
            // User not logged in or no permissions data
            toast.error('Please login to access this page');
            navigate('/auth/login', { replace: true });
            return;
        }

        // Normalize requiredPermissions to array
        const permissions = Array.isArray(requiredPermissions)
            ? requiredPermissions
            : [requiredPermissions];

        // Check if user has all required permissions
        const hasPermission = permissions.every(permission =>
            loginData.permissions.includes(permission)
        );

        if (!hasPermission) {
            toast.error('You do not have permission to access this page');
            navigate(redirectTo, { replace: true });
        }
    }, [requiredPermissions, navigate, redirectTo]);

    // Get user data again for rendering (after useEffect check)
    const loginData = JSON.parse(localStorage.getItem('User'));

    if (!loginData || !loginData.permissions) {
        return null; // Don't render anything while redirecting
    }

    // Normalize requiredPermissions to array
    const permissions = Array.isArray(requiredPermissions)
        ? requiredPermissions
        : [requiredPermissions];

    // Check if user has all required permissions
    const hasPermission = permissions.every(permission =>
        loginData.permissions.includes(permission)
    );

    // Only render children if user has permission
    return hasPermission ? children : null;
};

export default ProtectedRoute;
